import { Edit2 } from "iconsax-reactjs";
import { Plus, X } from "lucide-react";
import { formatPermissionLabel } from "../../../../shared/lib";
import {
  Button,
  Input,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalTitle,
} from "../../../../shared/ui";

type RoleFormModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  submitLabel: string;
  roleName: string;
  onRoleNameChange: (value: string) => void;
  roleDescription: string;
  onRoleDescriptionChange: (value: string) => void;
  selectedPermissions: string[];
  onOpenPermissions: () => void;
  onSubmit: () => void;
  onCancel: () => void;
  submitDisabled?: boolean;
  isSubmitting?: boolean;
  footerMessage?: string | null;
  messageClassName?: string;
};

const RoleFormModal = ({
  open,
  onOpenChange,
  title,
  submitLabel,
  roleName,
  onRoleNameChange,
  roleDescription,
  onRoleDescriptionChange,
  selectedPermissions,
  onOpenPermissions,
  onSubmit,
  onCancel,
  submitDisabled,
  isSubmitting,
  footerMessage,
  messageClassName = "text-[#6B6F8A]",
}: RoleFormModalProps) => {
  const permissionPills = selectedPermissions.slice(0, 3);
  const remainingPermissionCount = Math.max(selectedPermissions.length - 3, 0);

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      contentClassName="max-w-[600px]"
    >
      <ModalHeader>
        <ModalTitle>{title}</ModalTitle>
        <button
          type="button"
          aria-label="Close modal"
          className="rounded-full p-2 text-[#737AA8] hover:bg-[#F2F4FF]"
          onClick={onCancel}
        >
          <X size={20} />
        </button>
      </ModalHeader>
      <ModalBody className="pt-4">
        <div className="space-y-6">
          <div>
            <div className="text-[14px] font-[450] text-[#2C2F44]">Role</div>
            <Input
              value={roleName}
              onChange={(event) => onRoleNameChange(event.target.value)}
              placeholder="Enter role"
              className="mt-2 h-12 rounded-[14px]"
            />
          </div>

          <div>
            <div className="text-[14px] font-[450] text-[#2C2F44]">
              Description
            </div>
            <textarea
              value={roleDescription}
              onChange={(event) => onRoleDescriptionChange(event.target.value)}
              placeholder="Describe the new role"
              className="mt-2 min-h-[120px] w-full rounded-[14px] bg-[#EEF1FB] px-4 py-3 text-[14px] font-[350] text-gray-30 placeholder:text-gray-20 focus:outline-none focus:ring-2 focus:ring-[#D9DEFF]"
            />
          </div>

          <div>
            <div className="text-[14px] font-[450] text-[#2C2F44]">
              Permissions
            </div>
            {selectedPermissions.length === 0 ? (
              <Button
                variant="outline"
                className="mt-2 h-12 rounded-[14px] border-[#E1E6FF] text-[#2C2F44]"
                onClick={onOpenPermissions}
              >
                <Plus size={16} />
                Add permissions
              </Button>
            ) : (
              <div className="mt-3 space-y-3">
                <div className="flex flex-wrap gap-2">
                  {permissionPills.map((permission) => (
                    <span
                      key={permission}
                      className="rounded-full bg-[#EEF1FB] px-4 py-2 text-[14px] font-[350] text-gray-30"
                    >
                      {formatPermissionLabel(permission)}
                    </span>
                  ))}
                  {remainingPermissionCount > 0 ? (
                    <span className="rounded-full bg-[#EEF1FB] px-4 py-2 text-[14px] font-[350] text-gray-30">
                      +{remainingPermissionCount} more
                    </span>
                  ) : null}
                </div>
                <Button
                  variant="outline"
                  className="h-11 rounded-[14px] border-[#E1E6FF] text-[#2C2F44]"
                  onClick={onOpenPermissions}
                >
                  <Edit2 size={16} />
                  Edit permissions
                </Button>
              </div>
            )}
          </div>
        </div>
      </ModalBody>
      <ModalFooter>
        <Button
          variant="ghost"
          className="h-11 rounded-[14px] px-6 text-[#2C2F44]"
          onClick={onCancel}
        >
          Cancel
        </Button>
        <Button
          className="h-11 rounded-[14px] px-6"
          onClick={onSubmit}
          disabled={submitDisabled}
        >
          {isSubmitting
            ? `${submitLabel.replace(/\.\.\.$/, "")}...`
            : submitLabel}
        </Button>
      </ModalFooter>
      {footerMessage ? (
        <div className={`px-6 pb-6 text-[14px] ${messageClassName}`}>
          {footerMessage}
        </div>
      ) : null}
    </Modal>
  );
};

export default RoleFormModal;
